/**
 * Settings → AI Connections: connect/probe allowlisted providers on a node.
 * Browser sends connectionKey only — never commands or secrets.
 */
import { useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import { AI_CONNECTIONS_DISPLAY } from '../lib/ai-connections-catalog';
import {
  authStateLabel,
  buildAiConnectionRows,
  connectionModeLabel,
  installedLabel,
  type AiConnectionStatusRow,
} from '../lib/ai-connections-view';
import type { CodexAutonomyStatusRow, NodePresence, ProviderUsageLatest } from '../lib/data';
import { errorMessage } from '../lib/mutations';

export interface AiConnectionsSettingsProps {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly nodes: readonly NodePresence[];
  readonly providerUsage: readonly ProviderUsageLatest[];
  readonly connectionStatuses: readonly AiConnectionStatusRow[];
  readonly codexAutonomyStatuses?: readonly CodexAutonomyStatusRow[];
  readonly onChanged: () => void;
}

export function AiConnectionsSettings({
  client,
  guard,
  orgId,
  nodes,
  providerUsage,
  connectionStatuses,
  codexAutonomyStatuses = [],
  onChanged,
}: AiConnectionsSettingsProps): ReactElement {
  const connected = nodes.filter((node) => node.status !== 'offline');
  const [nodeId, setNodeId] = useState<string>(connected[0]?.node_id ?? nodes[0]?.node_id ?? '');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [codexBusy, setCodexBusy] = useState(false);

  const codexStatus = useMemo(
    () => codexAutonomyStatuses.find((status) => status.node_id === nodeId) ?? null,
    [codexAutonomyStatuses, nodeId],
  );

  const rows = useMemo(() => buildAiConnectionRows({
    catalog: AI_CONNECTIONS_DISPLAY,
    statuses: connectionStatuses,
    usage: providerUsage,
    nodeId: nodeId || null,
  }), [connectionStatuses, providerUsage, nodeId]);

  async function setCodexUnrestricted(enabled: boolean): Promise<void> {
    setError(null);
    setOk(null);
    if (!nodeId) {
      setError('Select a connected computer first.');
      return;
    }
    await guard('set_codex_unrestricted', async () => {
      setCodexBusy(true);
      try {
        const { data, error: transportError } = await client.rpc('set_codex_unrestricted', {
          request: { orgId, nodeId, enabled },
        });
        const result = data as { ok?: boolean; error?: { code?: string }; value?: { alreadyWaiting?: boolean } } | null;
        if (transportError || !result?.ok) {
          setError(errorMessage(result?.error?.code ?? 'internal'));
          return;
        }
        setOk(result.value?.alreadyWaiting
          ? 'That Codex profile change is already waiting on the computer.'
          : enabled
            ? 'Unrestricted Codex requested. The switch turns ON only after the computer reports full access.'
            : 'Bounded Codex requested. The switch turns OFF only after the computer reports workspace-write.');
        onChanged();
      } finally {
        setCodexBusy(false);
      }
    });
  }

  async function enqueue(action: 'probe_usage' | 'connect', connectionKey: string): Promise<void> {
    setError(null);
    setOk(null);
    if (!nodeId) {
      setError('Select a connected computer first.');
      return;
    }
    setBusyKey(`${action}:${connectionKey}`);
    try {
      const { data, error: transportError } = await client.rpc('enqueue_ai_connection_action', {
        request: { orgId, nodeId, action, connectionKey },
      });
      const result = data as { ok?: boolean; error?: { code?: string }; value?: { alreadyWaiting?: boolean } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return;
      }
      const local = AI_CONNECTIONS_DISPLAY.find((row) => row.connectionKey === connectionKey)?.meterMode === 'local_unlimited';
      setOk(result.value?.alreadyWaiting
        ? 'Already waiting on that computer.'
        : action === 'connect'
          ? 'Connect requested on the computer. Complete sign-in there if prompted.'
          : local
            ? 'Local runtime check requested on the computer.'
            : 'Usage refresh requested on the computer.');
      onChanged();
    } finally {
      setBusyKey(null);
    }
  }

  const autonomyLabel = (value: string | null | undefined) => value?.replaceAll('_', ' ') ?? 'unknown';

  return (
    <div className="stack ai-connections-settings" aria-label="AI Connections">
      <p className="muted">
        Check installed AI connections on a computer and refresh real usage readings.
        Local runtimes do not require sign-in. Credentials stay on that computer, and Brunel never invents a percent.
      </p>
      <label>
        Computer
        <select
          aria-label="Computer for AI connections"
          value={nodeId}
          onChange={(event) => setNodeId(event.target.value)}
        >
          {nodes.length === 0 && <option value="">No computers</option>}
          {nodes.map((node) => (
            <option key={node.node_id} value={node.node_id}>
              {node.name} ({node.status})
            </option>
          ))}
        </select>
      </label>
      <section className="stack" aria-label="Codex autonomy">
        <div className="row spread">
          <div>
            <strong>Unrestricted Codex</strong>
            <div><small className="muted">Explicit only. OFF keeps workspace-write with network; ON requests full filesystem/process access.</small></div>
          </div>
          <label className="row">
            <input
              aria-label="Unrestricted Codex"
              type="checkbox"
              role="switch"
              checked={codexStatus?.unrestricted === true}
              disabled={codexBusy || !nodeId}
              onChange={(event) => { void setCodexUnrestricted(event.target.checked); }}
            />
            <span>{codexStatus?.unrestricted === true ? 'ON' : 'OFF'}</span>
          </label>
        </div>
        <div className="profile-settings-grid">
          <div><small className="muted">Command approvals</small><strong>{autonomyLabel(codexStatus?.command_approvals)}</strong></div>
          <div><small className="muted">Sandbox</small><strong>{autonomyLabel(codexStatus?.sandbox)}</strong></div>
          <div><small className="muted">Network</small><strong>{autonomyLabel(codexStatus?.network)}</strong></div>
          <div><small className="muted">Browser approvals</small><strong>{autonomyLabel(codexStatus?.browser_approvals)}</strong></div>
          <div><small className="muted">Computer Use approvals</small><strong>{autonomyLabel(codexStatus?.computer_use_approvals)}</strong></div>
          <div><small className="muted">Plugin/app approvals</small><strong>{autonomyLabel(codexStatus?.plugin_approvals)}</strong></div>
          <div><small className="muted">Effective source</small><strong>{autonomyLabel(codexStatus?.effective_source)}</strong></div>
        </div>
        {codexStatus?.managed && <p role="status">Managed Codex policy is constraining at least one requested setting.</p>}
        <small className="muted">
          {codexStatus?.detail ?? 'No effective Codex report from this computer yet. Save supported Browser/Computer Use approvals in ChatGPT desktop settings; OS privacy grants remain outside Brunel.'}
        </small>
      </section>
      <div className="stack ai-connection-rows">
        {rows.map((row) => (
          <article key={row.connectionKey} className="ai-connection-row" aria-label={row.displayLabel}>
            <div className="row spread">
              <strong>{row.displayLabel}</strong>
              <small className="muted">{row.meterLabel}</small>
            </div>
            <div className="row wrap muted" style={{ gap: '0.75rem' }}>
              <span>{installedLabel(row.installed)}</span>
              <span>{row.meterMode === 'local_unlimited' ? 'No sign-in required' : authStateLabel(row.authState)}</span>
              <span>{connectionModeLabel(row.connectionMode)}</span>
              <span>{row.sourceLabel}</span>
            </div>
            {row.note && <small>{row.note}</small>}
            {row.lastReading && <small>Last reading: {row.lastReading}</small>}
            {row.lastProbeAt && <small className="muted">Probed {row.lastProbeAt}</small>}
            {row.lastError && <small className="muted">Probe note: {row.lastError}</small>}
            <div className="row" style={{ gap: '0.5rem' }}>
              {row.canRefreshUsage && (
                <button
                  type="button"
                  disabled={busyKey !== null || !nodeId}
                  onClick={() => { void enqueue('probe_usage', row.connectionKey); }}
                >
                  {row.meterMode === 'local_unlimited'
                    ? (busyKey === `probe_usage:${row.connectionKey}` ? 'Checking…' : 'Check local')
                    : (busyKey === `probe_usage:${row.connectionKey}` ? 'Refreshing…' : 'Refresh usage')}
                </button>
              )}
              {row.canConnect && (
                <button
                  type="button"
                  disabled={busyKey !== null || !nodeId}
                  onClick={() => { void enqueue('connect', row.connectionKey); }}
                >
                  {busyKey === `connect:${row.connectionKey}` ? 'Starting…' : 'Connect'}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      {error && <p role="alert">{error}</p>}
      {ok && <p className="muted">{ok}</p>}
    </div>
  );
}
